import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { ManageInWorkspace, workspaceGitLine } from './ManageInWorkspace';
import { confirmConnected } from './PersonalConnect';
import { isVersionControl, versionControlStatus } from './versionControl';
import { useAsync } from '../../pages/aai/useAsync';
import type { ConnectorCatalog, ConnectorRow, ConnectorScope } from '../../types/aai';
import { Action, Chip, Divided, EmptyState, ErrorNote, ListRow, Loading, Panel, PanelHead, RouteMark } from '../aa';
import { statusChip } from '../../pages/owner/ConnectionDrawer';

/*
 * Settings, Connections.
 *
 * Reads the ownership-aware catalog (GET /aai/connectors), which reports the
 * CALLER'S OWN connection state per provider and the organization's shared
 * connections, and never another member's personal connection. The legacy
 * client-wide connections list this tab used to call predates connection
 * ownership and showed every row to every role; it is gone from here.
 *
 *   Your connections      what this person has connected themselves
 *   Company connections   shared with the company, managed with
 *                         aai_manage_connectors. Version control (GitHub,
 *                         GitLab) is connected once for the company in the
 *                         Workspace, so it always sits here with the
 *                         Workspace's real state, never a Composio one.
 */

const Row: React.FC<{
  row: ConnectorRow;
  scope: ConnectorScope;
  status: string | null;
  canAct: boolean;
  onChanged: () => void;
}> = ({ row, scope, status, canAct, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const pollRef = useRef<number>(0);
  const versionControl = isVersionControl(row);
  const chip = statusChip(versionControl ? versionControlStatus(row) : status);
  const connected = status === 'connected';
  let foot: React.ReactNode = row.category_label;
  if (versionControl) foot = `${row.category_label} · ${workspaceGitLine(row)}`;

  useEffect(() => () => window.clearInterval(pollRef.current), []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await aaiApi.initiateConnector(row.slug, scope);
      if (data.alreadyConnected) {
        toast.info(`${row.label} is already connected.`);
        setBusy(false);
        onChanged();
        return;
      }
      if (!data.redirectUrl) {
        toast.error(`Could not start connecting ${row.label}. Try again in a moment.`);
        setBusy(false);
        return;
      }
      const popup = window.open(data.redirectUrl, `connect-${row.slug}`, 'width=600,height=720,menubar=no,toolbar=no');
      if (!popup || popup.closed) {
        window.location.href = data.redirectUrl;
        return;
      }
      pollRef.current = window.setInterval(async () => {
        if (!popup.closed) return;
        window.clearInterval(pollRef.current);
        const ok = await confirmConnected(row.slug, scope);
        setBusy(false);
        onChanged();
        if (ok) {
          toast.success(`${row.label} connected.`);
          return;
        }
        toast.error(`${row.label} did not finish connecting. If you closed the window early, try again.`);
      }, 900);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || `Could not start connecting ${row.label}.`);
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await aaiApi.revokeConnector(row.slug, scope);
      toast.success(`${row.label} disconnected.`);
      onChanged();
    } catch {
      toast.error(`Could not disconnect ${row.label}.`);
    } finally {
      setBusy(false);
    }
  };

  const action = (): React.ReactNode => {
    if (!canAct) return undefined;
    if (!row.enabled) return <Chip tone="warn">Switched off for the company</Chip>;
    if (versionControl || !row.connect_here) return <ManageInWorkspace row={row} />;
    if (connected) return <Action size="sm" variant="quiet" onClick={disconnect} disabled={busy} reason="Working">Disconnect</Action>;
    return <Action size="sm" variant="quiet" onClick={connect} disabled={busy} reason="Working">Connect</Action>;
  };

  return (
    <ListRow
      mark={<RouteMark kind="connection" size="md" />}
      title={row.label}
      badge={<Chip tone={chip.tone} dot>{chip.label}</Chip>}
      foot={foot}
      right={action()}
    />
  );
};

const ConnectionsSettings: React.FC = () => {
  const catalog = useAsync<ConnectorCatalog>(() => aaiApi.getConnectors(), 'Could not load connections.');

  if (catalog.loading) return <Loading label="Loading connections" />;
  if (catalog.error || !catalog.data) return <ErrorNote message={catalog.error || 'Something went wrong.'} />;

  const data = catalog.data;
  if (!data.can_use && !data.can_manage) {
    return <EmptyState title="Connections are not part of your role" reason="Ask the company Owner or AI Champion if you need to connect an app." />;
  }

  const mine = data.connectors.filter((c) => c.personal_status);
  const shared = data.connectors.filter((c) => c.organization_status || (c.enabled && isVersionControl(c)));
  const available = data.connectors.filter((c) => c.enabled && c.connect_here && !isVersionControl(c));

  return (
    <div className="space-y-5">
      {data.can_use && (
        <Panel>
          <PanelHead kicker="Your connections" title="Apps you connected yourself" desc="A personal connection runs as you, through your own account. Nobody else in the company sees it." />
          {mine.length === 0 && <EmptyState title="Nothing connected yet" reason="Connect an app below and your personal skills and routines can use it." />}
          <Divided>
            {mine.map((c) => <Row key={c.slug} row={c} scope="personal" status={c.personal_status} canAct={data.can_use} onChanged={catalog.reload} />)}
          </Divided>
        </Panel>
      )}

      {(data.can_manage || shared.length > 0) && (
        <Panel>
          <PanelHead kicker="Company connections" title="Shared with the company" desc="A company connection is shared by its owner for Projects and Quick Wins. Seeing it is not permission to browse it." />
          {shared.length === 0 && <EmptyState title="No company connections yet" reason="Connect an app for the company below. It is shared explicitly; personal connections never become company ones." />}
          <Divided>
            {shared.map((c) => <Row key={c.slug} row={c} scope="organization" status={c.organization_status} canAct={data.can_manage} onChanged={catalog.reload} />)}
          </Divided>
        </Panel>
      )}

      <Panel>
        <PanelHead
          kicker="Available"
          title={data.can_manage ? 'Connect an app for the company' : 'Connect an app'}
          desc={data.can_manage ? 'Connecting here shares the app with the company. Apps switched off for the company are not offered.' : 'Connecting here connects the app for you alone.'}
        />
        <Divided>
          {available.map((c) => (
            <Row
              key={c.slug}
              row={c}
              scope={data.can_manage ? 'organization' : 'personal'}
              status={data.can_manage ? c.organization_status : c.personal_status}
              canAct
              onChanged={catalog.reload}
            />
          ))}
        </Divided>
      </Panel>
    </div>
  );
};

export default ConnectionsSettings;
